import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { computeProjectDetailStats } from '../utils/computeProjectDetailStats'
import {
  buildFYOptions,
  getCurrentFYStartYear,
  type PeriodFilter,
} from '../utils/projectAnalysisPeriod'
import { buildMonthlyChartData } from '../utils/projectDetailChartData'
import { useProjectGoldFact } from './useProjectGoldFact'
import type { ProjectDetailTab } from '../types/projectDetailPage'

export function useProjectDetailPage() {
  const { projectId: rawId } = useParams<{ projectId: string }>()
  const projectId = rawId ? parseInt(rawId, 10) : NaN

  const { data: projectRawData, loading, error } = useProjectGoldFact(
    Number.isFinite(projectId) ? projectId : null,
  )

  const [detailMode, setDetailMode] = useState<'fy' | 'calendar'>('fy')
  const [detailFY, setDetailFY] = useState('2025-26')
  const [detailQ, setDetailQ] = useState('all')
  const [detailYear, setDetailYear] = useState('all')
  const [detailMonth, setDetailMonth] = useState('all')
  const [activeTab, setActiveTab] = useState<ProjectDetailTab>('overview')
  const [expandedResourceId, setExpandedResourceId] = useState<number | null>(
    null,
  )

  const detailFilter: PeriodFilter = useMemo(() => {
    if (detailMode === 'fy')
      return { year: 'all', month: 'all', fy: detailFY, q: detailQ }
    return { year: detailYear, month: detailMonth, fy: 'all', q: 'all' }
  }, [detailMode, detailFY, detailQ, detailYear, detailMonth])

  const projectStats = useMemo(
    () => computeProjectDetailStats(projectRawData, detailFilter),
    [projectRawData, detailFilter],
  )

  const detailYearOptions = useMemo(() => {
    if (!projectRawData?.length) return []
    return [...new Set(projectRawData.map((d) => d.year))]
      .filter(Boolean)
      .sort((a, b) => b - a) as number[]
  }, [projectRawData])

  const detailFYOptions = useMemo(() => {
    const years = detailYearOptions
    return buildFYOptions(
      years.length ? years : [new Date().getFullYear()],
      getCurrentFYStartYear(),
    )
  }, [detailYearOptions])

  useEffect(() => {
    if (
      Number.isFinite(projectId) &&
      detailFYOptions.length > 0 &&
      !detailFYOptions.some((o) => o.value === detailFY)
    ) {
      setDetailFY(detailFYOptions[0]?.value ?? '2025-26')
      setDetailQ('all')
    }
  }, [projectId, detailFYOptions, detailFY])

  const monthlyChartData = useMemo(
    () =>
      projectStats ? buildMonthlyChartData(projectStats.monthlyData) : [],
    [projectStats],
  )

  const projectDetailName =
    projectStats?.projectInfo?.project_name ||
    (Number.isFinite(projectId) ? `Project_${projectId}` : 'project')

  return {
    projectId,
    projectRawData,
    loading,
    error,
    detailMode,
    setDetailMode,
    detailFY,
    setDetailFY,
    detailQ,
    setDetailQ,
    detailYear,
    setDetailYear,
    detailMonth,
    setDetailMonth,
    activeTab,
    setActiveTab,
    expandedResourceId,
    setExpandedResourceId,
    projectStats,
    detailYearOptions,
    detailFYOptions,
    monthlyChartData,
    projectDetailName,
  }
}
